import {
    ContentItemReferenceWorkspaceItem,
    ContentRevisionReferenceWorkspaceItem,
    HtmlContent,
    InternalReferenceWorkspaceItem,
    VisibilityConditionOptions,
    WorkspaceGroup,
    WorkspaceItem,
} from '../store/common/workspaceScheme/types';
import { WorkspaceSchemeModel } from '../store/workspaces/types';

import i18nService from './i18nService';
import workspaceParser from './workspaceParser';
import { UserPrivilegesGetter } from './permissions/typings';
import commonApiService from './api/commonApiService';
import { WorkspacesState } from '../store/workspaces/reducer';

let userPrivilegesGetter: UserPrivilegesGetter;

export const configureWorkspaceSchemeService = (userPrivsGetter: UserPrivilegesGetter): void => {
    userPrivilegesGetter = userPrivsGetter;
};

const commonWorkspaceSchemeService = {
    async hasWorkspaceHtmlContent(workspaceItem: WorkspaceItem): Promise<boolean> {
        const workspaceRef = this.getWorkspaceIdentifier(workspaceItem);
        const workspaceConfiguration: any = await commonApiService.fetchWorkspace(workspaceRef);
        const featureConfig = workspaceParser.getFeatureConfig(workspaceConfiguration.main, 'htmlView');

        return !!featureConfig;
    },
    parseScheme(workspaceSchemeContent: WorkspaceSchemeModel): WorkspaceSchemeModel {
        if (workspaceSchemeContent === null) {
            return null;
        }
        const workspaceScheme: WorkspaceSchemeModel = {} as WorkspaceSchemeModel;
        workspaceScheme.name = workspaceSchemeContent.name;
        workspaceScheme.schemeId = workspaceSchemeContent.schemeId;
        workspaceScheme.workspaceGroups = [] as WorkspaceGroup[];
        workspaceScheme.workspaceGroups = [...workspaceSchemeContent.workspaceGroups];

        workspaceScheme.htmlContents = [] as HtmlContent[];
        if (workspaceSchemeContent.htmlContents) {
            workspaceScheme.htmlContents = [...workspaceSchemeContent.htmlContents];
        }
        return workspaceScheme;
    },
    getWorkspace(workspaceId: string, workspaceScheme: WorkspaceSchemeModel): WorkspaceItem {
        let workspace = null;

        // eslint-disable-next-line no-restricted-syntax
        for (const workspaceGroup of workspaceScheme.workspaceGroups) {
            workspace = workspaceGroup.workspaces
                .find((wSpace: WorkspaceItem) => workspaceId === this.getWorkspaceIdentifier(wSpace));
            if (workspace) {
                break;
            }
        }
        return workspace;
    },
    getFirstVisibleWorkspace(workspaceGroups: WorkspaceGroup[]): WorkspaceItem {
        if (!workspaceGroups) {
            return null;
        }

        let workspaceFound: WorkspaceItem = null;
        for (let i = 0; i < workspaceGroups.length; i += 1) {
            workspaceFound = workspaceGroups[i].workspaces.find((workspace: WorkspaceItem) => workspace.visible);
            if (workspaceFound) {
                break;
            }
        }
        return workspaceFound;
    },
    getWorkspaceType(workspaceId: string, workspaceScheme: WorkspaceSchemeModel): string {
        let type = '';
        // eslint-disable-next-line no-restricted-syntax
        if (workspaceScheme) {
            for (const workspaceGroup of workspaceScheme.workspaceGroups) {
                const workspaceFound = workspaceGroup.workspaces.find((workspace: WorkspaceItem) => {
                    return this.getWorkspaceIdentifier(workspace) === workspaceId;
                });

                if (workspaceFound) {
                    type = workspaceGroup.type;
                    break;
                }
            }
        }
        return type;
    },
    isWorkspaceVisible(workspaceGroups: WorkspaceGroup[], workspaceId: string): boolean {
        let workspaceFound: WorkspaceItem = null;
        for (let i = 0; i < workspaceGroups.length; i += 1) {
            workspaceFound = workspaceGroups[i].workspaces.find((workspace: WorkspaceItem) => this.getWorkspaceIdentifier(workspace) === workspaceId);
            if (workspaceFound) {
                break;
            }
        }
        return workspaceFound && workspaceFound.visible;
    },
    isContentItemReferenceWorkspace(workspaceItem: WorkspaceItem): boolean {
        return !!(workspaceItem as ContentItemReferenceWorkspaceItem).contentItemId;
    },
    isContentRevisionReferenceWorkspace(workspaceItem: WorkspaceItem): boolean {
        return !!(workspaceItem as ContentRevisionReferenceWorkspaceItem).contentRevisionId;
    },
    isInternalReferenceWorkspace(workspaceItem: WorkspaceItem): boolean {
        return !!(workspaceItem as InternalReferenceWorkspaceItem).internal_ref;
    },

    getWorkspaceIdentifier(workspaceItem: WorkspaceItem): string {
        if (this.isInternalReferenceWorkspace(workspaceItem)) {
            return (workspaceItem as InternalReferenceWorkspaceItem).internal_ref;
        }
        if (this.isContentItemReferenceWorkspace(workspaceItem)) {
            return (workspaceItem as ContentItemReferenceWorkspaceItem).contentItemId.toString();
        }
        return (workspaceItem as ContentRevisionReferenceWorkspaceItem).contentRevisionId.toString();
    },
    getNameForLanguage: (name: any): string => {
        const initialLanguage = i18nService.getInitialLanguage();

        return name[i18nService.getCurrentLanguageKey(initialLanguage)];
    },
    getWorkspaceVisibilityConditionType(workspaceItem: WorkspaceItem): string {
        if (workspaceItem.visibleWhen) {
            return VisibilityConditionOptions.VISIBLE_WHEN;
        }
        if (workspaceItem.visibleFrom) {
            return VisibilityConditionOptions.VISIBLE_FROM;
        }
        return VisibilityConditionOptions.BOOLEAN;
    },
    getAvailableWorkspacesForUser(workspaces: WorkspacesState): WorkspaceGroup[] {
        const { workspaceScheme } = workspaces;
        const { workspaceGroups } = workspaceScheme;
        const currentPrivileges = userPrivilegesGetter.getCurrentUserPrivileges();
        const filteredWorkspaceGroups: WorkspaceGroup[] = [];
        workspaceGroups.forEach((workspaceGroup: WorkspaceGroup) => {
            // We add the workspaceGroup only if we have it's permission
            if (currentPrivileges.includes(workspaceGroup.type) && !workspaceGroup.extraGroup) {
                const wSpaces: WorkspaceItem[] = [];
                workspaceGroup.workspaces.forEach((workspace: WorkspaceItem) => {
                    if (workspace.visible || workspace.visibleWhen || workspace.visibleFrom) {
                        wSpaces.push(workspace);
                    }
                });

                // We want to keep out the empty workspaces groups even if the user has permission
                if (wSpaces.length > 0) {
                    filteredWorkspaceGroups.push({
                        workspaces: wSpaces,
                        type: workspaceGroup.type,
                    });
                }
            }
        });
        return filteredWorkspaceGroups;
    },
};

export default commonWorkspaceSchemeService;
